import { useContext } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import {
  Bars3BottomLeftIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartPieIcon,
  CheckIcon,
  CogIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Resizable } from 're-resizable';

import { useLocalStorage } from '@ftrprf/hooks';

import { ActionStyledAsButton } from '../../buttons/ActionStyledAsButton';
import useFormatMessage from '../../../hooks/useFormatMessage';

import { ProgressBar } from '../../progress/ProgressBar';
import {
  SlideViewerContext,
  SlideViewerLeftAction,
  SlideViewerRightAction,
  SlideViewerSlideNumber,
} from '../../slideviewer/SlideViewer';

import SideBarSettings from './SideBarSettings';
import SideBarSlidesOverview from './SideBarSlidesOverview';
import SideBarTeacherInfo from './SideBarTeacherInfo';
import Tabs from './Tabs';
import { UserGroupInfo } from './UserGroupInfo';
import { ExampleAnswer } from './ExampleAnswer';
import { MultipleChoiceExampleAnswer } from './MultipleChoiceExampleAnswer';

const enable = {
  bottom: false,
  bottomLeft: false,
  bottomRight: false,
  left: false,
  right: true,
  top: false,
  topLeft: false,
  topRight: false,
};

const standardWidth = 385;

export default function SideBar({
  currentSlide,
  feedbackUrl,
  lesson,
  showFeedbackDialog,
  showTeacherInfoTab,
  steamsTab,
}) {
  const t = useFormatMessage();

  const [sidebarSize, setSidebarSize] = useLocalStorage(
    'SLIDE_VIEWER_SIDEBAR_SIZE',
    {
      width: standardWidth,
    },
  );

  const SteamsTab = steamsTab;

  const {
    currentSlideIndex,
    isCk5,
    isTeacher,
    quickplan,
    slides,
    toggleQuickplan,
    user,
    userGroupInfo,
  } = useContext(SlideViewerContext);
  const { isStudent } = user;

  const solution =
    currentSlide.question?.solution || currentSlide?.question?.[0]?.solution;
  const multipleChoiceSolution =
    currentSlide?.question?.questionAnswersMultipleChoice?.length ||
    currentSlide?.questions?.[0]?.questionAnswersMultipleChoice?.length;
  const showMultipleChoiceSolution = multipleChoiceSolution && isTeacher;
  const showSolution = solution && !isStudent;

  return (
    <Resizable
      className="border-r border-gray-300 overflow-hidden flex flex-col self-stretch bg-white z-10"
      data-test="slideviewer-sidebar"
      defaultSize={sidebarSize}
      enable={enable}
      maxWidth="50%"
      minWidth={standardWidth}
      onResizeStop={(e, direction, ref, d) => {
        setSidebarSize((s) => ({
          width: s.width + d.width,
        }));
      }}
    >
      <div className="flex justify-between pt-6 px-4">
        <h2 className="text-2xl font-bold leading-none">{lesson?.title}</h2>
        <div className="flex flex-shrink-0 items-center text-2xl">
          <SlideViewerLeftAction className="text-2xl">
            <FaChevronLeft />
          </SlideViewerLeftAction>
          <SlideViewerRightAction className="text-2xl">
            <FaChevronRight />
          </SlideViewerRightAction>
        </div>
      </div>

      <ProgressBar
        black
        className="mx-4 mt-5"
        maximumProgress={slides.length - 1}
        progress={currentSlideIndex}
      />

      <Tabs
        containerClassName="flex flex-col overflow-hidden flex-grow tabsContainer"
        footerContainerClassName="flex border-t border-gray-300 px-4 pt-2 pb-1 justify-between items-center footerContainer mt-auto"
        footerExtra={
          <div className="flex items-center footerExtra">
            <div className="mr-4">
              {feedbackUrl && !user?.isStudent && (
                <div className="mb-2">
                  <ActionStyledAsButton
                    onClick={showFeedbackDialog}
                    secondary
                    test="feedbackButton"
                  >
                    {t('styled-slide-viewer.settings.feedback')}
                  </ActionStyledAsButton>
                </div>
              )}
            </div>
            <div className="text-xs">
              <SlideViewerSlideNumber />
            </div>
          </div>
        }
        items={[
          {
            button: {
              icon: <BookOpenIcon className="w-6 h-6" />,
              id: 'teacher_manual',
              label: t('styled-slide-viewer.teacher_manual'),
            },
            content: <SideBarTeacherInfo currentSlide={currentSlide} />,
            show: showTeacherInfoTab,
          },
          {
            button: {
              icon: <Bars3BottomLeftIcon className="w-6 h-6" />,
              id: 'table_of_contents',
              label: t('styled-slide-viewer.table_of_contents'),
            },
            content: <SideBarSlidesOverview />,
            show: true,
          },
          {
            button: {
              icon: <CheckIcon className="w-6 h-6" />,
              id: 'example_answer',
              label: t('styled-slide-viewer.example_answer'),
            },
            content: (
              <div className="h-full">
                {showSolution && <ExampleAnswer solution={solution} />}
                {showMultipleChoiceSolution && (
                  <MultipleChoiceExampleAnswer
                    currentSlide={currentSlide}
                    isTeacher={isTeacher}
                  />
                )}
              </div>
            ),
            show: showSolution || showMultipleChoiceSolution,
          },
          {
            button: {
              icon: <CalendarIcon className="w-6 h-6" />,
              id: 'quickplan',
              label: t('styled-slide-viewer.quickplan'),
              onClick: toggleQuickplan,
            },
            content: (
              <div className="overflow-hidden flex flex-col flex-grow px-4">
                <h3 className="mt-4 mb-1 text-2xl font-bold">
                  {t('styled-slide-viewer.quickplan')}
                </h3>
                <ActionStyledAsButton
                  className="mt-4 w-fit"
                  onClick={toggleQuickplan}
                >
                  <CalendarIcon className="w-6 h-6 mr-2" />
                  {t('styled-slide-viewer.openQuickplan')}
                </ActionStyledAsButton>
              </div>
            ),
            show: isTeacher && quickplan,
          },
          {
            button: {
              icon: <CogIcon className="w-6 h-6" />,
              id: 'settings',
              label: t('styled-slide-viewer.settings'),
            },
            content: <SideBarSettings />,
            show: !isCk5,
          },
          {
            button: {
              icon: <UserGroupIcon className="w-6 h-6" />,
              id: 'userGroupInfo',
              label: t('styled-slide-viewer.userGroupInfo'),
            },
            content: <UserGroupInfo userGroupInfo={userGroupInfo} />,
            show: userGroupInfo,
          },
          {
            button: {
              icon: <ChartPieIcon className="w-6 h-6" />,
              id: 'steams',
              label: t('styled-slide-viewer.steams'),
            },
            content: <SteamsTab />,
            show: !!steamsTab,
          },
        ]}
        labelClassName="px-2 mr-4 pb-px border-b-2 border-transparent opacity-50"
        labelClassNameActive="px-2 pb-px mr-4 border-b-2 border-accent-500"
        labelsContainerClassName="flex"
      />
    </Resizable>
  );
}
